<template>
  <div class="market-updates-archive">
    <div class="archive-header">
      <h1 class="archive-title">Monthly Market Updates Archive</h1>
      <p class="archive-subtitle">Educational insights and market analysis for informed investment decisions</p>
    </div>

    <!-- Search and Filter Section -->
    <div class="filter-section">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by title..."
          class="search-input"
        />
      </div>

      <div class="tags-filter">
        <button
          v-for="tag in availableTags"
          :key="tag"
          class="tag-pill"
          :class="{ 'tag-active': selectedTags.includes(tag) }"
          @click="toggleTag(tag)"
        >
          {{ formatTag(tag) }}
        </button>
        <button
          v-if="selectedTags.length > 0"
          class="clear-filters-btn"
          @click="clearFilters"
        >
          Clear Filters
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading market updates...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <i class="pi pi-exclamation-circle"></i>
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadUpdates">Retry</button>
    </div>

    <!-- Updates Grid -->
    <div v-else-if="filteredUpdates.length > 0" class="updates-grid">
      <div
        v-for="update in filteredUpdates"
        :key="update.id"
        class="update-card"
        @click="navigateToUpdate(update.id)"
      >
        <div class="card-header">
          <h3 class="card-title">{{ update.title }}</h3>
          <span class="card-date">{{ formatDate(update.asOfDate) }}</span>
        </div>
        <p class="card-description">{{ update.description }}</p>
        <div class="card-tags">
          <span
            v-for="tag in update.tags"
            :key="tag"
            class="card-tag"
          >
            {{ formatTag(tag) }}
          </span>
        </div>
        <div class="card-footer">
          <span class="published-label">Published: {{ formatDate(update.publishedAt) }}</span>
          <span class="read-more">Read Update →</span>
        </div>
      </div>
    </div>

    <!-- No Results State -->
    <div v-else class="empty-state">
      <i class="pi pi-inbox"></i>
      <p>No market updates found</p>
      <small v-if="searchQuery || selectedTags.length > 0">Try adjusting your filters</small>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

// State
const updates = ref([]);
const isLoading = ref(false);
const error = ref(null);
const searchQuery = ref('');
const selectedTags = ref([]);

// Define emits for parent communication
const emit = defineEmits(['navigate-to-update']);

// Computed
const availableTags = computed(() => {
  const tagSet = new Set();
  updates.value.forEach(update => {
    update.tags?.forEach(tag => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
});

const filteredUpdates = computed(() => {
  let filtered = updates.value.filter(update => update.status === 'published');

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(update =>
      update.title.toLowerCase().includes(query) ||
      update.description?.toLowerCase().includes(query)
    );
  }

  // Tag filter
  if (selectedTags.value.length > 0) {
    filtered = filtered.filter(update =>
      selectedTags.value.every(tag => update.tags?.includes(tag))
    );
  }

  // Sort by date (newest first)
  return filtered.sort((a, b) => new Date(b.asOfDate) - new Date(a.asOfDate));
});

// Methods
const loadUpdates = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    // Use dynamic import to ensure the file is bundled correctly
    const indexModule = await import('@/assets/contents/market-updates/index.json');
    const data = indexModule.default || indexModule;
    updates.value = data.updates || [];
  } catch (err) {
    console.error('Error loading updates:', err);
    error.value = 'Failed to load market updates. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

const toggleTag = (tag) => {
  const index = selectedTags.value.indexOf(tag);
  if (index > -1) {
    selectedTags.value.splice(index, 1);
  } else {
    selectedTags.value.push(tag);
  }
};

const clearFilters = () => {
  searchQuery.value = '';
  selectedTags.value = [];
};

const formatTag = (tag) => {
  return tag.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const navigateToUpdate = (id) => {
  // Emit event to parent component to handle navigation
  emit('navigate-to-update', id);
};

// Lifecycle
onMounted(() => {
  loadUpdates();
});
</script>

<style scoped>
.market-updates-archive {
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
}

/* Header */
.archive-header {
  text-align: center;
  margin-bottom: 32px;
}

.archive-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.archive-subtitle {
  font-size: 1rem;
  color: var(--an-text-muted);
  margin: 0;
}

/* Filter Section */
.filter-section {
  background: white;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-box {
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--an-primary);
}

.tags-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-pill {
  padding: 6px 12px;
  background: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--an-text-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.tag-pill:hover {
  background: #e0e0e0;
}

.tag-pill.tag-active {
  background: var(--an-primary);
  color: white;
  border-color: var(--an-primary);
}

.clear-filters-btn {
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #dc3545;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #dc3545;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-filters-btn:hover {
  background: #dc3545;
  color: white;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--an-text-muted);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid var(--an-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error & Empty States */
.error-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--an-text-muted);
}

.error-state i,
.empty-state i {
  font-size: 3rem;
  margin-bottom: 16px;
  display: block;
  color: #dc3545;
}

.empty-state i {
  color: var(--an-text-muted);
}

.retry-btn {
  margin-top: 16px;
  padding: 10px 20px;
  background: var(--an-primary);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
}

/* Updates Grid */
.updates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.update-card {
  background: white;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.update-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-4px);
}

.card-header {
  margin-bottom: 12px;
}

.card-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 6px 0;
  line-height: 1.3;
}

.card-date {
  font-size: 0.85rem;
  color: var(--an-primary);
  font-weight: 600;
}

.card-description {
  font-size: 0.9rem;
  color: var(--an-text-muted);
  line-height: 1.5;
  margin: 0 0 12px 0;
  flex: 1;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.card-tag {
  padding: 4px 8px;
  background: #f0f4f8;
  border-radius: 4px;
  font-size: 0.75rem;
  color: var(--an-primary);
  font-weight: 500;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.published-label {
  font-size: 0.8rem;
  color: var(--an-text-muted);
}

.read-more {
  font-size: 0.85rem;
  color: var(--an-primary);
  font-weight: 600;
}

/* Responsive */
@media (max-width: 768px) {
  .market-updates-archive {
    padding: 12px;
  }

  .archive-title {
    font-size: 1.5rem;
  }

  .archive-subtitle {
    font-size: 0.9rem;
  }

  .filter-section {
    padding: 14px;
  }

  .updates-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .update-card {
    padding: 14px;
  }
}

@media (max-width: 480px) {
  .market-updates-archive {
    padding: 10px;
  }

  .archive-title {
    font-size: 1.25rem;
  }

  .filter-section {
    padding: 12px;
  }

  .update-card {
    padding: 12px;
  }

  .card-title {
    font-size: 1rem;
  }

  .card-description {
    font-size: 0.85rem;
  }
}
</style>
